import _ from 'lodash';
import React, {Component} from 'react';
import {connect} from 'react-redux';
import {
  CartesianGrid,
  Legend,
  Line,
  LineChart,
  ReferenceArea,
  ReferenceLine,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis
} from 'recharts';
import CustomisedDot from './CustomisedDot';

const CIRCLE = 1;
const LINE = 2;
const V_LINE = 3;
const V_LINE_DASH = 4;

class LineChartGraph extends Component {

  // onSeriesClick = (graph, series) => {
  //     const {dispatch} = this.props;
  //     dispatch(toggleGraphSeries(graph, series));
  // };

  renderCustomisedLegend = (props) => {
    const { graph } = props;
    return (
      <div className="c-graphs__legend" style={{ fontSize: '11px' }}>
        <ul>
          {graph.series.map((series, i) => {
            let icon;
            if (series.dotType) {
              icon = <CustomisedDot dotType={series.dotType} legend={true}/>;
            } else {
              icon = this.drawIcon(series.dot ? CIRCLE : LINE, series.stroke);
            }
            return <li style={{ display: 'inline-block', marginRight: '10px' }}
                       key={i}>{icon} <span>{series.name}</span></li>;
          })}
          <li style={{ display: 'inline-block', marginRight: '10px' }}
              key="now">{this.drawIcon(V_LINE, '#FF0000')} <span>Now</span></li>
          <li style={{ display: 'inline-block', marginRight: '10px' }}
              key="selected">{this.drawIcon(V_LINE_DASH, '#FF0000')} <span>Selected</span></li>
        </ul>
      </div>
    );
  };

  drawIcon = (type, seriesStroke) => {
    let dot;
    switch (type) {
      case CIRCLE:
        dot = 'M0,16 H6 A5,5,0,1,1,16,16 H22 M16,16 A5,5,0,1,1,6,16';
        break;
      case V_LINE:
        dot = 'M16,0 V32';
        break;
      case V_LINE_DASH:
        dot = 'M16,0 V6 M16,12 V18 M16,24 V30';
        break;
      case LINE:
      default:
        dot = 'M0,16 H32';
        break;
    }
    return (<svg width="14" height="14" viewBox="0 0 32 32"
                 version="1.1"
                 style={{ display: 'inline-block', verticalAlign: 'middle', marginRight: '4px' }}>
      <path strokeWidth="4" fill="none" stroke={seriesStroke} d={dot}
            className="recharts-legend-icon"/>
    </svg>);
  };

  formatTick = (tick) => {
    return tick.replace(' 12:00 AM', '');
  };

  render() {
    // dotDensity only affects series with a dotType. If set, it specifies which dots should be displayed. 1 = all, 2 =
    // every second etc.
    const { data, ticks, graph, dotDensity, now_x, selected_time_x, onGraphClick } = this.props;
    // If the graph has hidden lines (stroke = none) then make sure they don't affect the YAxisTicks.
    let yAxisTicks;
    if (!graph.YAxisTicks && _.some(graph.series, s => 'none' === s.stroke)) {
      const series = _.filter(graph.series, s => 'none' !== s.stroke);
      let min = 0;
      let max = 0;
      data.forEach(d => {
        series.forEach(s => {
          const v = d[s.dataKey];
          if (v && v < min) {
            min = v;
          }
          if (v && v > max) {
            max = v;
          }
        });
      });

      min = Math.floor(min);
      max = Math.ceil(max);
      const tickSize = Math.ceil((max - min) / 4.0);
      yAxisTicks = [min, min + tickSize, min + 2 * tickSize, min + 3 * tickSize, min + 4 * tickSize];
    } else {
      yAxisTicks = graph.YAxisTicks;
    }

    return <ResponsiveContainer width="100%" height={250}>
      <LineChart data={data}
                 margin={{ top: 0, right: 5, bottom: 20, left: 5 }}
                 onClick={onGraphClick.bind(null, data)}>
        <CartesianGrid strokeDasharray="3 3"/>
        <ReferenceLine x={now_x}
                       stroke="red"/>
        <ReferenceLine x={selected_time_x}
                       stroke="red"
                       strokeDasharray="3 3"/>
        {graph.maxLine && <ReferenceLine y={graph.maxLine} stroke="#B0DD6C" label="Max"/>}
        {graph.maxLine &&
         <ReferenceArea y1={graph.minLine} y2={graph.maxLine} fill="#B0DD6C" ifOverflow="extendDomain"/>}
        {graph.minLine && <ReferenceLine y={graph.minLine} stroke="#B0DD6C" label="Min"/>}
        {
          graph.thresholds && graph.thresholds.map((threshold, i) => (
                             <ReferenceArea key={i} y1={threshold.lowerValue} y2={threshold.upperValue}
                                            fill={threshold.color} fillOpacity={0.3}
                                            alwaysShow={!!threshold.alwaysShow}/>
                           ))
        }
        <XAxis dataKey="name"
               tick={{ angle: 0, fontSize: 11 }}
               textAnchor="start"
               ticks={ticks}
               tickFormatter={this.formatTick}
               interval={0}
        />
        {yAxisTicks && yAxisTicks.length > 0 &&
         <YAxis tick={{ fontSize: 11 }}
                ticks={yAxisTicks}
                tickCount={yAxisTicks ? yAxisTicks.length : 5}
                domain={[yAxisTicks[0], yAxisTicks[yAxisTicks.length - 1]]}
                allowDataOverflow={true}
                interval={0}
                label={{ value: graph.YAxisLabel, angle: -90, position: 'insideLeft', fontSize: 12 }}/>
        }
        {!yAxisTicks &&
         <YAxis tick={{ fontSize: 11 }}
                label={{ value: graph.YAxisLabel, angle: -90, position: 'insideLeft', fontSize: 12 }}/>
        }
        <Tooltip/>
        <Legend verticalAlign="top" graph={graph} content={this.renderCustomisedLegend}
                wrapperStyle={{ paddingBottom: 10 }}/>
        {
          graph.series.map((series, i) => {
            let line;
            if (series && series.active) {
              let dot = false;
              if (series.dotType) {
                dot = <CustomisedDot
                  dotType={series.dotType}
                  dotValueKey={series.dotValueKey}
                  density={dotDensity}
                />;
              } else if (series.dot) {
                dot = series.dot;
              }

              line = <Line id="line"
                           connectNulls={true}
                           key={i}
                           name={series.name}
                           type={series.type}
                           dataKey={series.dataKey}
                           stroke={series.stroke}
                           strokeDasharray={series.strokeDasharray}
                           isAnimationActive={false}
                           dot={dot}/>;
            }
            return line;
          })
        }
      </LineChart>
    </ResponsiveContainer>;
  }
}

const mapStateToProps = state => {
  return {
    ...state
  };
};

const mapDispatchToProps = dispatch => {
  return {
    dispatch: dispatch
  };
};

export default connect(mapStateToProps, mapDispatchToProps)(LineChartGraph);



// WEBPACK FOOTER //
// ./src/components/graphs/LineChartGraph.js